import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Coffee,
  Smartphone,
  Loader2,
  User,
  Phone,
  Sparkles,
  ArrowRight,
  RefreshCw,
  QrCode,
} from "lucide-react";
import { orpc } from "@/utils/orpc";
import { env } from "@pos-vidyon/env/web";

export const Route = createFileRoute("/loyalty/register/$orgSlug")({
  component: LoyaltyRegisterPage,
});

function LoyaltyRegisterPage() {
  const { orgSlug } = Route.useParams();

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [registeredCardId, setRegisteredCardId] = useState<string | null>(null);

  // Check if they already have a card saved in this browser's localStorage
  useEffect(() => {
    const savedCardId = localStorage.getItem(`vidyon_loyalty_card_${orgSlug}`);
    if (savedCardId) {
      setRegisteredCardId(savedCardId);
    }
  }, [orgSlug]);

  // Query to fetch the pass details dynamically if the card is registered
  const {
    data: cardData,
    isLoading: isCardLoading,
    refetch: refetchCard,
  } = useQuery({
    ...orpc.loyalty.getCardPublic.queryOptions({ input: { cardId: registeredCardId ?? "" } }),
    enabled: !!registeredCardId,
  });

  // Mutation for registration
  const registerMutation = useMutation({
    ...orpc.loyalty.registerCard.mutationOptions({
      onSuccess: (data) => {
        setRegisteredCardId(data.cardId);
        localStorage.setItem(`vidyon_loyalty_card_${orgSlug}`, data.cardId);
      },
      onError: (err) => {
        alert(err.message || "Kayıt olurken bir hata oluştu.");
      },
    }),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    registerMutation.mutate({
      organizationSlug: orgSlug,
      name: name.trim(),
      phone: phone.trim() || null,
      email: null,
    });
  };

  const serverUrl = env.VITE_SERVER_URL?.endsWith("/")
    ? env.VITE_SERVER_URL.slice(0, -1)
    : env.VITE_SERVER_URL || "http://localhost:3000";

  const appleWalletLink = `${serverUrl}/api/loyalty/pass/apple/${registeredCardId}`;
  const googleWalletLink = `${serverUrl}/api/loyalty/pass/google/${registeredCardId}`;

  // Design tokens based on fetched settings, or fallback to default
  const settings = cardData?.settings || {
    cardTitle: "Sadakat Kartı",
    backgroundColor: "#5c4033",
    foregroundColor: "#ffffff",
    labelColor: "#dddddd",
    targetStamps: 10,
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1e1b18] to-[#0f0e0d] flex items-center justify-center p-4 font-sans text-white">
      {/* Background decoration elements */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(92,64,51,0.15)_0,transparent_60%)] pointer-events-none" />

      <div className="w-full max-w-md rounded-3xl bg-white/5 border border-white/10 backdrop-blur-xl p-6 sm:p-8 shadow-2xl relative overflow-hidden animate-in fade-in zoom-in-95 duration-500">
        {/* Top Logo / Welcome */}
        <div className="text-center mb-6">
          <div className="inline-flex size-12 items-center justify-center rounded-2xl bg-gradient-to-tr from-amber-700 to-amber-900 text-white shadow-xl shadow-amber-950/20 mb-3 animate-bounce">
            <Coffee className="size-6" />
          </div>
          <h1 className="text-xl font-black tracking-tight bg-gradient-to-r from-white via-gray-200 to-gray-400 bg-clip-text text-transparent">
            Sadakat Programı
          </h1>
          <p className="text-[10px] text-gray-400 mt-1 uppercase tracking-widest font-semibold">
            {orgSlug.replace("-", " ")}
          </p>
        </div>

        {isCardLoading && registeredCardId ? (
          <div className="flex flex-col items-center justify-center py-12 space-y-3">
            <Loader2 className="size-8 animate-spin text-amber-500" />
            <span className="text-xs text-gray-400">Dijital kartınız yükleniyor...</span>
          </div>
        ) : !registeredCardId ? (
          /* REGISTRATION FORM */
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-4">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1.5 pl-1">
                  Adınız ve Soyadınız
                </label>
                <div className="relative">
                  <User className="absolute left-4 top-3.5 size-4 text-gray-500" />
                  <input
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-2xl py-3 pl-11 pr-4 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-amber-600 focus:ring-1 focus:ring-amber-600 transition-all"
                    placeholder="Örn: Ahmet Yılmaz"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1.5 pl-1">
                  Telefon Numaranız
                </label>
                <div className="relative">
                  <Phone className="absolute left-4 top-3.5 size-4 text-gray-500" />
                  <input
                    type="tel"
                    required
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-2xl py-3 pl-11 pr-4 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-amber-600 focus:ring-1 focus:ring-amber-600 transition-all"
                    placeholder="Örn: 05551234567"
                  />
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={registerMutation.isPending}
              className="w-full flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-amber-600 to-amber-800 hover:from-amber-500 hover:to-amber-700 py-3.5 text-sm font-bold text-white shadow-xl shadow-amber-950/20 active:scale-[0.98] disabled:opacity-50 disabled:pointer-events-none transition-all mt-4"
            >
              {registerMutation.isPending ? (
                <Loader2 className="size-5 animate-spin" />
              ) : (
                <>
                  <span>Katıl & Kartı Oluştur</span>
                  <ArrowRight className="size-4" />
                </>
              )}
            </button>

            <p className="text-[10px] text-center text-gray-500 mt-2 leading-relaxed">
              Kaydolarak her alışverişinizde damga kazanma ve bedava ürün kazanma koşullarını kabul
              etmiş olursunuz.
            </p>
          </form>
        ) : (
          /* SUCCESS SCREEN & LIVE TICKET PREVIEW */
          <div className="text-center space-y-6 animate-in fade-in duration-500">
            {/* Live Web Ticket Preview */}
            {cardData && (
              <div
                className="w-full rounded-2xl shadow-2xl p-5 flex flex-col justify-between overflow-hidden text-left relative transition-all duration-300 border border-white/10"
                style={{
                  backgroundColor: settings.backgroundColor,
                  color: settings.foregroundColor,
                }}
              >
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="flex size-7 items-center justify-center rounded-lg bg-white/20 backdrop-blur-md">
                      <Coffee className="size-4" style={{ color: settings.foregroundColor }} />
                    </div>
                    <span className="text-xs font-extrabold uppercase tracking-wide opacity-90">
                      {settings.cardTitle}
                    </span>
                  </div>
                  <button
                    onClick={() => refetchCard()}
                    className="text-[10px] opacity-75 hover:opacity-100 flex items-center gap-1 bg-white/15 px-2 py-0.5 rounded-md active:scale-95 transition-all"
                    title="Yenile"
                  >
                    <RefreshCw className="size-2.5 animate-spin-hover" />
                    <span>Güncelle</span>
                  </button>
                </div>

                {/* Body - Stamps */}
                <div className="my-6 space-y-4">
                  <div className="text-center">
                    <span
                      className="text-[8px] uppercase font-bold tracking-wider opacity-60"
                      style={{ color: settings.labelColor }}
                    >
                      Kazanılan Damgalar
                    </span>
                    <div className="text-3xl font-black mt-0.5">
                      {cardData.card.stampsCount} / {settings.targetStamps}
                    </div>
                  </div>

                  {/* Stamp cups grid */}
                  <div className="grid grid-cols-5 gap-2 max-w-[280px] mx-auto justify-center">
                    {Array.from({ length: settings.targetStamps }).map((_, i) => {
                      const isStamped = i < cardData.card.stampsCount;
                      return (
                        <div
                          key={i}
                          className={`aspect-square rounded-full border flex items-center justify-center transition-all ${
                            isStamped ? "bg-white/30 border-white" : "bg-black/15 border-white/15"
                          }`}
                        >
                          <Coffee
                            className={`size-3.5 ${isStamped ? "opacity-100 scale-115 text-white" : "opacity-25"}`}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Validation Code Bar */}
                <div className="bg-white rounded-xl p-3 flex flex-col items-center gap-2.5 shadow-lg text-black text-center">
                  <div className="text-[9px] uppercase tracking-wider text-gray-400 font-bold flex items-center gap-1">
                    <QrCode className="size-3" />
                    Kasa Onay Kodu
                  </div>

                  {/* Real, scannable Code 128 barcode image */}
                  <img
                    src={`https://bwipjs-api.metafloor.com/?bcid=code128&text=${cardData.card.currentCode}&scale=2.5&rotate=N&includetext=false`}
                    alt="Barcode"
                    className="h-10 object-contain my-1 select-none"
                    loading="lazy"
                  />

                  <span className="text-sm font-black tracking-widest text-blue-900 leading-none">
                    {cardData.card.currentCode}
                  </span>
                  <span className="text-[8px] text-gray-400">
                    Güvenliğiniz için bu kod her işlem sonrası değişir.
                  </span>
                </div>

                {/* Footer fields */}
                <div className="flex justify-between items-center mt-4 pt-3 border-t border-white/10 text-[11px]">
                  <div>
                    <span
                      className="block text-[8px] uppercase font-bold opacity-60"
                      style={{ color: settings.labelColor }}
                    >
                      Müşteri
                    </span>
                    <span className="font-bold">{cardData.customer.name}</span>
                  </div>
                  <div className="text-right">
                    <span
                      className="block text-[8px] uppercase font-bold opacity-60"
                      style={{ color: settings.labelColor }}
                    >
                      Hediye Ürünler
                    </span>
                    <span className="font-bold text-emerald-300 font-extrabold">
                      {cardData.card.rewardCount} Bedava
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Wallet Download Links */}
            <div className="space-y-3 pt-2">
              <div className="text-xs text-gray-400 flex items-center justify-center gap-1">
                <Smartphone className="size-3.5" />
                <span>Kartınızı Cüzdan Uygulamasına Ekleyin:</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {/* Apple Wallet Badge */}
                <a
                  href={appleWalletLink}
                  className="flex items-center justify-center bg-black hover:bg-neutral-900 border border-white/20 rounded-2xl py-2.5 px-3 shadow-xl active:scale-[0.98] transition-all"
                  title="Apple Wallet'a Ekle"
                >
                  <div className="flex items-center gap-1.5 text-left">
                    <svg
                      className="size-5 text-white shrink-0"
                      viewBox="0 0 40 40"
                      fill="currentColor"
                    >
                      <path d="M30 6H10C7.8 6 6 7.8 6 10v20c0 2.2 1.8 4 4 4h20c2.2 0 4-1.8 4-4V10c0-2.2-1.8-4-4-4zm-2 19H12v-2h16v2zm0-5H12v-2h16v2zm0-5H12V13h16v2z" />
                    </svg>
                    <div className="leading-tight">
                      <span className="block text-[8px] text-gray-400 font-bold uppercase tracking-wider">
                        Add to
                      </span>
                      <span className="block text-xs font-black tracking-tight">Apple Wallet</span>
                    </div>
                  </div>
                </a>

                {/* Google Wallet Badge */}
                <a
                  href={googleWalletLink}
                  className="flex items-center justify-center bg-[#131314] hover:bg-neutral-900 border border-white/10 rounded-2xl py-2.5 px-3 shadow-xl active:scale-[0.98] transition-all"
                  title="Google Wallet'a Kaydet"
                >
                  <div className="flex items-center gap-2 text-left">
                    <div className="flex shrink-0 gap-[1px]">
                      <div className="w-1 h-3.5 bg-[#ea4335] rounded-l" />
                      <div className="w-1 h-3.5 bg-[#f9ab00]" />
                      <div className="w-1 h-3.5 bg-[#34a853]" />
                      <div className="w-1 h-3.5 bg-[#4285f4] rounded-r" />
                    </div>
                    <div className="leading-tight">
                      <span className="block text-[8px] text-gray-400 font-bold uppercase tracking-wider">
                        Save to
                      </span>
                      <span className="block text-xs font-black tracking-tight">Google Wallet</span>
                    </div>
                  </div>
                </a>
              </div>
            </div>

            <div className="flex items-center justify-center gap-1 text-[9px] text-gray-500 pt-2">
              <Sparkles className="size-3 text-amber-500" />
              <span>Kartınız kasada işlem yapıldıkça eş zamanlı güncellenir.</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
